import { useEffect } from 'react'

// Wraps the Screen Wake Lock API. Pass `enabled=true` to keep the screen
// on; flip to `false` (or unmount) to release. Browsers auto-release on
// tab-hide, so we re-acquire on visibilitychange while still enabled.
// Failures (unsupported browser, missing user gesture, page hidden) are
// swallowed — the caller can probe `wakeLockSupported()` to hide the UI.
export const useWakeLock = (enabled: boolean): void => {
	useEffect(() => {
		if (!enabled) return
		if (!('wakeLock' in navigator)) return

		let sentinel: WakeLockSentinel | null = null
		let cancelled = false

		const acquire = async () => {
			if (document.visibilityState !== 'visible') return
			try {
				const s = await navigator.wakeLock.request('screen')
				if (cancelled) {
					void s.release()
					return
				}
				sentinel = s
				// Browser auto-releases on tab-hide; clear our ref so the
				// visibilitychange handler re-acquires when we're foreground.
				s.addEventListener('release', () => {
					if (sentinel === s) sentinel = null
				})
			} catch {
				// Ignore — best-effort.
			}
		}

		const onVisibility = () => {
			if (document.visibilityState === 'visible' && !sentinel) {
				void acquire()
			}
		}

		void acquire()
		document.addEventListener('visibilitychange', onVisibility)

		return () => {
			cancelled = true
			document.removeEventListener('visibilitychange', onVisibility)
			if (sentinel) {
				void sentinel.release().catch(() => {})
				sentinel = null
			}
		}
	}, [enabled])
}

export const wakeLockSupported = (): boolean =>
	typeof navigator !== 'undefined' && 'wakeLock' in navigator
